import { useMemo } from 'react'
import * as flags from 'country-flag-icons/react/3x2'
import {
	getCountryNameByIso2,
	getIso2FromCountryName,
} from 'shared/utils/countryNameByIso2'
import { applicationsStore } from '../../../../ApplicationsPage/model/applicationsStore'
import { getStudentCareerInterests } from 'pages/Students/constants/studentCareerInterests'
import { useMediaQuery } from 'react-responsive'

const usePersonalData = ({ studentData }) => {
	const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
	const isTablet = useMediaQuery({ query: '(max-width: 1199px)' })
	const {
		id,
		name,
		grade,
		phone,
		email,
		country,
		target_countries,
		dream_country,
		career,
		category,
		sub_category,
		avatarKey,
		avatarSource,
		photoUrl,
	} = studentData || {}

	const countriesList = useMemo(() => {
		let raw = []

		if (Array.isArray(target_countries) && target_countries.length > 0) {
			raw = target_countries
		} else if (dream_country) {
			raw = Array.isArray(dream_country) ? dream_country : [dream_country]
		}

		// Flatten comma-joined values so each country becomes its own pill.
		const expanded = raw
			.flatMap(item => String(item || '').split(','))
			.map(item => item.trim())
			.filter(Boolean)

		return [...new Set(expanded)]
	}, [target_countries, dream_country])

	const countriesWithFlags = useMemo(() => {
		// target_countries may mix ISO2, full names, and city/location strings
		// (e.g. "Southfield"). Keep only values that resolve to a known ISO2
		// with a flag asset — same format used for label + data-country BG.
		const seen = new Set()
		const result = []

		for (const rawName of countriesList) {
			const iso2 = getIso2FromCountryName(rawName)
			if (!iso2 || seen.has(iso2)) continue

			const FlagIcon = flags[iso2]
			if (!FlagIcon) continue

			seen.add(iso2)
			result.push({
				name: getCountryNameByIso2(iso2),
				iso2,
				FlagIcon,
			})
		}

		return result
	}, [countriesList])

	// Get universities from applications for this student
	const universitiesList = useMemo(() => {
		if (!id) return []

		const studentApplications = applicationsStore.applications.filter(
			app => String(app.studentId) === String(id),
		)

		// Extract unique universities
		const uniqueUniversities = [
			...new Set(
				studentApplications.map(app => app.university).filter(Boolean),
			),
		]

		return uniqueUniversities
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, applicationsStore.applications])

	const careersList = useMemo(
		() => getStudentCareerInterests({ career, category, sub_category }),
		[career, category, sub_category],
	)

	return {
		name,
		grade,
		phone,
		email,
		country,
		countryList: countriesWithFlags,
		universitiesList,
		careersList,
		isMobile,
		isTablet,
		avatarKey,
		avatarSource,
		photoUrl,
	}
}

export default usePersonalData
